import { useState, useContext, useEffect } from "react";
import { Switch, Route, Redirect } from "react-router-dom";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import { ModulePage } from "../pages/v2/ModulePage";
import { HomePage as LearningOverview } from "../pages/v2/HomePage";
import { ExplorePage } from "../pages/v2/ExplorePage";
import { UnauthorizedPage } from "../pages/UnauthorizedPage";
import { TermsAndConditionsPage } from "../pages/TermsAndConditionsPage";
import { TopAppBar as NewTopBar } from "./v2/TopAppBar";
import { CourseStructureContext } from "../context/CourseStructureContext";
import { UserContext } from "../context/UserContext";
import { ActivityType, Role } from "../enums";
import { CourseStructureCtx, UserCtx } from "../interfaces";
import { ProtectedRoute } from "./ProtectedRoute";
import { UsersPage } from "../pages/v2/UsersPage";
import { LoginHeader as NewLoginHeader } from "./v2/login/LoginHeader";
import { NotificationPage } from "../pages/v2/NotificationPage";
import { LtiDeepLinkingPage } from "../pages/v2/LtiDeepLinkingPage";
import { MiniSideDrawer } from "./v3/MiniSideDrawer";
import { TermsAndConditionsText } from "./TermsAndConditionsText";
import { HomePage as ClassOverview } from "../pages/v3/HomePage";
import { InsightsRouter } from "./v3/insights/InsightsRouter";
import { Lti1p3Lxp } from "./lti1p3/Lti1p3Lxp";
import { FAQPage } from "../pages/v3/FAQPage";
import { NotificationSettingsPage } from "../pages/v2/NotificationSettingsPage";
import { Box, Stack } from "@mui/material";
import { CanvasSync } from "./v3/canvas-sync/CanvasSync";
import { ScheduledMaintenance } from "../pages/ScheduledMaintenancePage";
import { formatTimeMst, formatDateMst } from "./utils/MSTDateTimeFormatter";

export const MacsRouter = () => {
  const [showProgress, setShowProgress] = useState(false);
  const courseStructureCtx = useContext(CourseStructureContext) as CourseStructureCtx;
  let currentQuestion = courseStructureCtx.courseTreeStructure?.getQuestion(
    courseStructureCtx.currentQuestionId
  );
  const currentCourse = courseStructureCtx.courseTreeStructure?.getCurrentCourse();

  const userCtx = useContext(UserContext) as UserCtx;

  // display progress spinner until crucial API for context are loaded
  useEffect(() => {
    if (
      userCtx.currentUserEndpointIsLoading === true ||
      courseStructureCtx?.modulesEndpointIsLoading === true
    ) {
      setShowProgress(true);
      return;
    } else {
      setShowProgress(false);
    }
  }, [
    userCtx?.currentUserEndpointIsLoading,
    courseStructureCtx?.modulesEndpointIsLoading,
  ]);

  if (showProgress || !userCtx.dbUser) {
    return (
      <Backdrop open>
        <CircularProgress />
      </Backdrop>
    );
  }

  if (userCtx.dbUser && !userCtx.dbUser?.accepted_tos) {
    return (
      <>
        <NewLoginHeader showLogin={false} />
        <TermsAndConditionsPage />
      </>
    );
  }

  const navBar = <NewTopBar />;

  const maintenanceWindow = userCtx.maintenanceWindow;
  const now = new Date();

  const underScheduledMaintenance = !!(
    maintenanceWindow &&
    new Date(maintenanceWindow.maintenance_from) <= now &&
    now <= new Date(maintenanceWindow.maintenance_to)
  );

  const toTime = maintenanceWindow
    ? formatTimeMst(maintenanceWindow.maintenance_to)
    : undefined;

  const toDate = maintenanceWindow
    ? formatDateMst(maintenanceWindow.maintenance_to)
    : undefined;

  const maintenanceMessage =
    maintenanceWindow?.custom_maintenance_message != null
      ? maintenanceWindow?.custom_maintenance_message
      : `This is scheduled maintenance to improve your experience. We expect to be back by ${toTime} MST on ${toDate}. Thanks for your patience while we work!`;

  return (
    <>
      <Switch>
        <Route
          exact
          path="/scheduled-maintenance"
          render={() =>
            underScheduledMaintenance ? (
              <Box
                sx={{
                  position: "relative",
                  display: "flex",
                  flexDirection: "column",
                  height: "100vh",
                }}>
                <NewLoginHeader showLogin={false} />
                <Box sx={{ position: "relative", flexGrow: 1 }}>
                  <ScheduledMaintenance
                    title="We’re making some tune-ups"
                    description={maintenanceMessage}
                  />
                </Box>
              </Box>
            ) : (
              <Redirect to="/" />
            )
          }
        />

        {underScheduledMaintenance && <Redirect to="/scheduled-maintenance" />}

        {/* ================= NORMAL APP ROUTES ================= */}

        <Route exact path="/error">
          <div>LTI token Expired</div>
        </Route>
        <Route
          exact
          path="/learning-overview"
          render={() => {
            if (userCtx.dbUser.role === Role.student) {
              return (
                <>
                  <NewTopBar />
                  <LearningOverview />
                </>
              );
            } else {
              return <Redirect to="/class-overview" />;
            }
          }}></Route>
        <ProtectedRoute
          exact
          path="/class-overview"
          //TODO: Remove the Role.student
          allowedRoles={[Role.super, Role.admin, Role.teacher, Role.student]}
          userRole={userCtx.dbUser.role}>
          <NewTopBar />
          <ClassOverview />
        </ProtectedRoute>
        <ProtectedRoute
          path="/insights/class/:class_id"
          allowedRoles={[Role.super, Role.admin, Role.teacher]}
          userRole={userCtx.dbUser.role}>
          <MiniSideDrawer>
            <InsightsRouter currentUser={userCtx.dbUser} />
          </MiniSideDrawer>
        </ProtectedRoute>
        {/* explore videos page doesn't exist pre-wrapper */}
        <Route path="/explore">
          {navBar}
          <ExplorePage />
        </Route>
        <ProtectedRoute
          exact={true}
          path="/notifications"
          allowedRoles={[Role.teacher]}
          userRole={userCtx.dbUser.role}>
          {navBar}
          <NotificationPage
            isNotificationEnabled={userCtx.dbUser.notifications_enabled}
          />
        </ProtectedRoute>
        <ProtectedRoute
          exact={true}
          path="/notifications/settings"
          allowedRoles={[Role.teacher]}
          userRole={userCtx.dbUser.role}>
          {navBar}
          <NotificationSettingsPage
            isNotificationEnabled={userCtx.dbUser.notifications_enabled}
            isEmailNotificationEnabled={userCtx.dbUser.email_notifications_enabled}
          />
        </ProtectedRoute>
        {/*<ProtectedRoute*/}
        {/*  path="/insights"*/}
        {/*  allowedRoles={[Role.super, Role.admin, Role.teacher]}*/}
        {/*  userRole={userCtx.dbUser.role}>*/}
        {/*  {navBar}*/}
        {/*  <InsightsRouter />*/}
        {/*  /!* <MiniSideDrawer>*/}
        {/*    <InsightsRouter />*/}
        {/*  </MiniSideDrawer> *!/*/}
        {/*</ProtectedRoute>*/}
        <Route exact path="/solver">
          {currentQuestion &&
          (currentQuestion.activity_type === ActivityType.orchard_node ||
            currentQuestion.activity_type === ActivityType.orchard_node_h5p ||
            currentQuestion.activity_type === ActivityType.lti1p3_problem ||
            currentQuestion?.activity_type === ActivityType.digit_problem) ? (
            <Lti1p3Lxp
              courseStructureCtx={courseStructureCtx}
              userCtx={userCtx}
              currentUser={userCtx.dbUser}
              currentQuestion={currentQuestion}
            />
          ) : (
            <Lti1p3Lxp
              courseStructureCtx={courseStructureCtx}
              userCtx={userCtx}
              currentUser={userCtx.dbUser}
              currentQuestion={currentQuestion}
              mode="1p0"
            />
            // <PostMessageProvider>
            //   <NewMacsWrapperPage
            //     showTopBar={true}
            //     userRole={userCtx.dbUser.role}
            //     contentToDisplay={ContentType.solver}
            //     solverRef={solverRef}
            //     isDeeplinkPreview={
            //       courseStructureCtx.backBehavior.route === "/lti/deep-linking"
            //     }
            //   />
            // </PostMessageProvider>
          )}
        </Route>
        {/* /targets route will largely be deprecated as the content is getting rolled up into the /modules path */}
        <Route path="/modules">
          <NewTopBar />
          <ModulePage />
        </Route>
        <ProtectedRoute
          path="/user-management"
          allowedRoles={[Role.super, Role.admin, Role.teacher]}
          userRole={userCtx.dbUser.role}>
          {navBar}
          <UsersPage
            tab={0}
            columnNames={
              userCtx.dbUser.role === Role.teacher || userCtx.dbUser.role === Role.admin
                ? [
                    "observer_ids",
                    "first_name",
                    "last_name",
                    "full_name",
                    "role",
                    "school_name",
                    "subject_period_combined",
                    "created_at",
                    "last_access_at",
                    "action",
                  ]
                : [
                    // super admin fields
                    "first_name",
                    "last_name",
                    "full_name",
                    "email",
                    "role",
                    "school_name",
                    "created_at",
                    "last_access_at",
                    "action",
                  ]
            }
          />
        </ProtectedRoute>
        <ProtectedRoute
          path="/class-management"
          allowedRoles={[Role.super, Role.admin, Role.teacher]}
          userRole={userCtx.dbUser.role}>
          {navBar}
          <UsersPage tab={1} />
        </ProtectedRoute>
        <ProtectedRoute
          path="/school-management"
          allowedRoles={[Role.super]}
          userRole={userCtx.dbUser.role}>
          {navBar}
          <UsersPage tab={2} />
        </ProtectedRoute>
        <ProtectedRoute
          path="/archived-user"
          allowedRoles={[Role.super, Role.admin, Role.teacher]}
          userRole={userCtx.dbUser.role}>
          {navBar}
          <UsersPage
            tab={3}
            defaultSortField="full_name"
            columnNames={
              userCtx.dbUser.role === Role.super
                ? [
                    "observer_ids",
                    "full_name",
                    "email",
                    "role",
                    "school_name",
                    "is_active",
                    "disabled_by",
                    "disabled_at",
                    "action",
                  ]
                : [
                    "observer_ids",
                    "full_name",
                    "role",
                    "school_name",
                    "is_active",
                    "disabled_by",
                    "disabled_at",
                    "action",
                  ]
            }
          />
        </ProtectedRoute>
        <ProtectedRoute
          path="/archived-class-management"
          allowedRoles={[Role.super, Role.admin, Role.teacher]}
          userRole={userCtx.dbUser.role}>
          {navBar}
          <UsersPage tab={4} hideTabs={true} hideHeader={true} />
        </ProtectedRoute>
        <ProtectedRoute
          path="/lti-tools"
          allowedRoles={[Role.super]}
          userRole={userCtx.dbUser.role}>
          {navBar}
          <UsersPage tab={5} />
        </ProtectedRoute>
        <ProtectedRoute
          path="/course-management"
          allowedRoles={[Role.super]}
          userRole={userCtx.dbUser.role}>
          {navBar}
          <UsersPage tab={6} />
        </ProtectedRoute>
        <ProtectedRoute
          path="/lti-assignments"
          allowedRoles={[Role.super]}
          userRole={userCtx.dbUser.role}>
          {navBar}
          <UsersPage tab={7} />
        </ProtectedRoute>
        <Route exact path="/lti/deep-linking">
          <>
            <LtiDeepLinkingPage />
          </>
        </Route>
        <Route exact path="/oAuth2/canvas">
          <Stack direction="column" height="100vh">
            <CanvasSync />
          </Stack>
        </Route>
        <Route exact path="/terms-of-service">
          <TermsAndConditionsText />
        </Route>
        <Route exact path="/faq">
          <Stack direction="column" height="100vh">
            <NewLoginHeader showLogin={false} showDemo={false} />
            <FAQPage key="faq-page" currentCourse={currentCourse} />
          </Stack>
        </Route>
        <Route exact path="/faq/search">
          <Stack direction="column" height="100vh">
            <NewLoginHeader showLogin={false} showDemo={false} />
            <FAQPage key="faq-page" search currentCourse={currentCourse} />
          </Stack>
        </Route>
        <Route exact path="/faq/:courseTag">
          <Stack direction="column" height="100vh">
            <NewLoginHeader showLogin={false} showDemo={false} />
            <FAQPage key="faq-page" singleCourseTag currentCourse={currentCourse} />
          </Stack>
        </Route>
        <Route path="/unauthorized">
          {navBar}
          <UnauthorizedPage />
        </Route>
        <Route
          path="/"
          render={({ history }) => {
            if (!userCtx?.dbUser) return null;
            const user = userCtx.dbUser;
            if ([Role.admin, Role.super, Role.teacher].includes(user?.role)) {
              history.replace("/class-overview");
              return null;
            } else if (user.user_progress && user.user_progress.length > 0) {
              // Redirect to the question the user left off on (if it exists)
              if (!currentCourse) {
                return null;
              }
              const { target, id } = user.user_progress[0].question;
              const allModules = currentCourse.getAllModules();
              const isProgressModuleInCurrentCourse = allModules.find((module) => {
                return module.id === target.module_id;
              });
              if (isProgressModuleInCurrentCourse) {
                history.replace({
                  pathname: "/solver",
                  search: new URLSearchParams({
                    questionId: id,
                  }).toString(),
                });
              } else {
                history.replace("/learning-overview");
              }
              return null;
            } else {
              history.replace("/learning-overview");
              return null;
            }
          }}
        />
      </Switch>
      {/* )} */}
    </>
  );
};
